import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export function useCart() {
  return useContext(CartContext);
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('porkCart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('porkCart', JSON.stringify(cartItems));
  }, [cartItems]);

  // cartKey format: productId_SIZE_COLOR
  // Using 'ns' for no-size and 'nc' for no-color as placeholders
  // This makes the key structure consistent and ready for color variants
  function makeCartKey(productId, size, color) {
    const s = size || 'ns';
    const c = color || 'nc';
    return `${productId}_${s}_${c}`;
  }

  // Add a product to the cart.
  // size:  selected size string ('S', 'M', 'L', 'XL') or null
  // color: selected color string or null — placeholder for future color selector
  // qty:   how many to add at once (defaults to 1)
  function addToCart(product, size = null, color = null, qty = 1) {
    const cartKey = makeCartKey(product.id, size, color);
    setCartItems(prev => {
      const existing = prev.find(item => item.cartKey === cartKey);
      if (existing) {
        return prev.map(item =>
          item.cartKey === cartKey
            ? { ...item, quantity: item.quantity + qty }
            : item
        );
      }
      return [
        ...prev,
        {
          ...product,
          cartKey,
          selectedSize: size,
          selectedColor: color,
          quantity: qty
        }
      ];
    });
  }

  // Remove a line item by its cartKey
  function removeFromCart(cartKey) {
    setCartItems(prev => prev.filter(item => item.cartKey !== cartKey));
  }

  // Update quantity of a specific line item
  function updateQuantity(cartKey, quantity) {
    if (quantity <= 0) {
      removeFromCart(cartKey);
      return;
    }
    setCartItems(prev =>
      prev.map(item =>
        item.cartKey === cartKey ? { ...item, quantity } : item
      )
    );
  }

  // Change the selected size of a cart item in place.
  // If the user already has that product+size combo, merge quantities instead of duplicating.
  function editCartItemSize(oldCartKey, newSize) {
    setCartItems(prev => {
      const oldItem = prev.find(item => item.cartKey === oldCartKey);
      if (!oldItem) return prev;

      const newCartKey = makeCartKey(oldItem.id, newSize, oldItem.selectedColor);
      if (newCartKey === oldCartKey) return prev; // nothing changed

      const existingWithNewKey = prev.find(item => item.cartKey === newCartKey);

      if (existingWithNewKey) {
        // Merge old quantity into the existing size variant, then remove old item
        return prev
          .filter(item => item.cartKey !== oldCartKey)
          .map(item =>
            item.cartKey === newCartKey
              ? { ...item, quantity: item.quantity + oldItem.quantity }
              : item
          );
      } else {
        // Just update the item in place with the new size and key
        return prev.map(item =>
          item.cartKey === oldCartKey
            ? { ...item, cartKey: newCartKey, selectedSize: newSize }
            : item
        );
      }
    });
  }

  function clearCart() {
    setCartItems([]);
  }

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const cartSubtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    editCartItemSize,
    clearCart,
    cartCount,
    cartSubtotal
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}
